import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ChevronDown, ChevronUp, Cookie, Settings } from 'lucide-react';
import { API_URL } from '../config';

// Texte hardcoded — NU se resetează la build
const DEFAULT_COOKIE_TEXTS = {
  text: 'Folosim cookie-uri pentru a personaliza conținutul și anunțurile, pentru a oferi funcții de rețele sociale și pentru a analiza traficul. De asemenea, le oferim partenerilor de rețele sociale, de publicitate și de analize informații cu privire la modul în care folosiți site-ul nostru. Aceștia le pot combina cu alte informații oferite de dvs. sau culese în urma folosirii serviciilor lor.',
  acceptButtonText: 'Permite toate',
  rejectButtonText: 'Doar necesare',
  customizeButtonText: 'Personalizează',
  aboutText: `Cookie-urile sunt mici fișiere de text ce pot fi utilizate de către site-urile web pentru a face utilizarea lor mai eficientă.

Legea stipulează că putem stoca cookie-uri pe dispozitivul dvs., în cazul în care ele sunt strict necesare pentru operarea acestui site. Pentru toate celelalte tipuri de cookie-uri avem nevoie de permisiunea dvs.

Acest site utilizează diferite tipuri de cookie-uri. Unele cookie-uri sunt plasate de către servicii părți terțe care apar pe paginile noastre.

Puteți, în orice moment, să modificați sau să vă retrageți acordul din Declarația privind modulele cookie de pe website-ul nostru.

Aflați mai multe despre cine suntem, cum ne puteți contacta și cum procesăm datele personale în Politica noastră de confidențialitate.

Când ne contactați în legatura cu consimțământul dvs., va rugăm să precizați ID-ul și data consimțământului dat.`,
  categories: {
    necessary: {
      name: 'Necesare',
      description: 'Cookie-urile necesare ajută la a face un site utilizabil prin activarea funcțiilor de bază, precum navigarea în pagină și accesul la zonele securizate de pe site. Site-ul nu poate funcționa corespunzător fără aceste cookie-uri.'
    },
    statistics: {
      name: 'Statistici',
      description: 'Cookie-urile de statistică îi ajută pe proprietarii unui site să înțeleagă modul în care vizitatorii interacționează cu site-urile prin colectarea și raportarea informațiilor în mod anonim.'
    },
    marketing: {
      name: 'Marketing',
      description: 'Cookie-urile de marketing sunt utilizate pentru a-i urmări pe utilizatori de la un site la altul. Intenția este de a afișa anunțuri relevante și antrenante pentru utilizatorii individuali, așadar ele sunt mai valoroase pentru agenții de publicitate și părțile terțe care se ocupă de publicitate.'
    },
    preferences: {
      name: 'Preferințe',
      description: 'Cookie-urile de preferință permit unui site să își amintească informații care se modifică după modul în care se comportă sau arată site-ul, precum limba dvs. preferată sau regiunea în care vă aflați.'
    }
  }
};

const CookieBanner = () => {
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [activeTab, setActiveTab] = useState('categories'); // 'categories' | 'about'

  // State pentru categorii selectate
  const [selectedCategories, setSelectedCategories] = useState({
    necessary: true,
    statistics: false,
    marketing: false,
    preferences: false
  });

  // State pentru expanded accordion
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Generează sau recuperează sessionId unic
  useEffect(() => {
    let existingSessionId = localStorage.getItem('cookieSessionId');

    if (!existingSessionId) {
      existingSessionId = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      localStorage.setItem('cookieSessionId', existingSessionId);
    }

    setSessionId(existingSessionId);
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const consentDecision = localStorage.getItem('cookieConsent');

        if (consentDecision) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/api/orders/cookie-consent`);
        const data = await response.json();

        // Texte HARDCODED - doar enabled/position/wallMode din API
        const merged = {
          ...DEFAULT_COOKIE_TEXTS,
          enabled: data.enabled,
          position: data.position || 'bottom',
          wallMode: data.wallMode || false
        };

        setSettings(merged);

        if (data.enabled) {
          setVisible(true);
        }
      } catch (error) {
        console.error('Error fetching cookie consent settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Trimite decizia la server pentru audit trail GDPR
  const logConsentToServer = async (categories) => {
    try {
      await fetch(`${API_URL}/api/orders/cookie-consent/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          categories,
          sessionId,
          bannerText: settings?.text,
          pageUrl: window.location.href,
          email: null
        })
      });
    } catch (error) {
      console.error('❌ [GDPR] Error logging consent to server:', error);
    }
  };

  const handleAcceptAll = async () => {
    const allCategories = {
      necessary: true,
      statistics: true,
      marketing: true,
      preferences: true
    };

    await logConsentToServer(allCategories);
    localStorage.setItem('cookieConsent', JSON.stringify(allCategories));
    window.dispatchEvent(new Event('cookieConsentChanged'));
    setVisible(false);
    setShowModal(false);
  };

  const handleRejectOptional = async () => {
    const onlyNecessary = {
      necessary: true,
      statistics: false,
      marketing: false,
      preferences: false
    };

    await logConsentToServer(onlyNecessary);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    window.dispatchEvent(new Event('cookieConsentChanged'));
    setVisible(false);
    setShowModal(false);
  };

  const handleSaveCustom = async () => {
    await logConsentToServer(selectedCategories);
    localStorage.setItem('cookieConsent', JSON.stringify(selectedCategories));
    window.dispatchEvent(new Event('cookieConsentChanged'));
    setVisible(false);
    setShowModal(false);
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const toggleCategory = (category) => {
    if (category === 'necessary') return; // Nu poate fi dezactivat

    setSelectedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleExpanded = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  // NU afișa cookie banner pe paginile admin
  const isAdminPage = location.pathname.startsWith('/admin');
  if (isAdminPage) return null;

  if (loading || !visible || !settings) {
    return null;
  }

  const positionClasses = settings.position === 'top' ? 'top-0' : 'bottom-0';

  // Cookie Wall Mode - modal centrat cu backdrop
  if (settings.wallMode && !showModal) {
    return (
      <>
        {/* Backdrop blocat */}
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[9998]" />

        {/* Modal centrat obligatoriu */}
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
            {/* Icon Cookie */}
            <div className="flex justify-center mb-4">
              <div className="bg-blue-100 rounded-full p-4">
                <svg className="w-12 h-12 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
              <Cookie className="w-6 h-6 inline-block align-text-bottom mr-1" aria-hidden="true" />Cookie-uri
            </h2>

            {/* Text */}
            <p className="text-sm text-gray-700 leading-relaxed text-center mb-6">
              {settings.text}
            </p>

            {/* Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleAcceptAll}
                className="w-full px-6 py-3 bg-green-600 text-white font-bold text-lg rounded-xl hover:bg-green-700 transition-colors shadow-lg"
              >
                ✓ {settings.acceptButtonText}
              </button>
              <button
                onClick={handleOpenModal}
                className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />{settings.customizeButtonText}
              </button>
            </div>

            {/* Info footer */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Pentru a continua, te rugăm să accepți cookies sau să personalizezi setările.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Banner Principal (normal mode) */}
      {!showModal && (
        <div
          className={`fixed left-0 right-0 ${positionClasses} z-50 bg-white border-gray-300 shadow-2xl animate-slide-in`}
          style={{
            borderTopWidth: settings.position === 'bottom' ? '2px' : '0',
            borderBottomWidth: settings.position === 'top' ? '2px' : '0',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1 flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {settings.text}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleRejectOptional}
                  className="px-4 py-2 bg-gray-400 text-white text-sm font-medium rounded-lg hover:bg-gray-500 transition-colors"
                >
                  {settings.rejectButtonText}
                </button>
                <button
                  onClick={handleOpenModal}
                  className="px-4 py-2 border-2 border-emerald-600 text-emerald-600 text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors"
                >
                  {settings.customizeButtonText}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  {settings.acceptButtonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Personalizează */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header Modal */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Personalizează preferințe</h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 px-6">
              <div className="flex gap-4">
                <button
                  onClick={() => setActiveTab('categories')}
                  className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'categories'
                      ? 'border-emerald-600 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Categorii
                </button>
                <button
                  onClick={() => setActiveTab('about')}
                  className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'about'
                      ? 'border-emerald-600 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Despre
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {activeTab === 'categories' && (
                <div className="space-y-4">
                  {/* Categorii Accordion */}
                  {Object.keys(settings.categories || {}).map((categoryKey) => {
                    const category = settings.categories[categoryKey];
                    const isExpanded = expandedCategory === categoryKey;
                    const isEnabled = selectedCategories[categoryKey];
                    const isNecessary = categoryKey === 'necessary';

                    return (
                      <div key={categoryKey} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Header Categorie */}
                        <div
                          className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => toggleExpanded(categoryKey)}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                            <span className="font-semibold text-gray-800">{category.name}</span>
                          </div>

                          {/* Toggle Switch */}
                          <label
                            className="relative inline-flex items-center cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={() => toggleCategory(categoryKey)}
                              disabled={isNecessary}
                              className="sr-only peer"
                            />
                            <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer ${
                              isNecessary ? 'opacity-50 cursor-not-allowed' : ''
                            } peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600`}></div>
                          </label>
                        </div>

                        {/* Descriere Categorie (Expanded) */}
                        {isExpanded && (
                          <div className="p-4 bg-white border-t border-gray-200">
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {category.description}
                            </p>
                            {isNecessary && (
                              <p className="mt-2 text-xs text-gray-500 italic">
                                * Cookie-urile necesare sunt întotdeauna active și nu pot fi dezactivate.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {activeTab === 'about' && (
                <div className="prose prose-sm max-w-none">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Despre cookie-uri</h3>
                    <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {settings.aboutText}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Modal */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-between">
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Permite toate
              </button>
              <button
                onClick={handleSaveCustom}
                className="px-6 py-2 border-2 border-emerald-600 text-emerald-600 font-medium rounded-lg hover:bg-blue-50 transition-colors"
              >
                Salvează preferințe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animație slide-in */}
      <style jsx>{`
        @keyframes slide-in-bottom {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes slide-in-top {
          from {
            transform: translateY(-100%);
          }
          to {
            transform: translateY(0);
          }
        }

        .animate-slide-in {
          animation: ${settings.position === 'top' ? 'slide-in-top' : 'slide-in-bottom'} 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default CookieBanner;
